import { Injectable }            from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	Router
}                                from '@angular/router';
import { AllowedDetailsTabEnum } from '@core/interfaces/common.interfaces';
import {
	ApplicationRoutes,
	IRouteDefinition,
	IRouteParams,
	RouteBasePath,
	RouteDefinition
}                                from '@core/interfaces/route.interface';
import { routerStore }           from '@core/store/router/router.store';
import {
	select,
	Store
}                                from '@ngrx/store';
import { LoggerService }         from '@shared/services/logger.service';
import { Observable }            from 'rxjs';
import {
	map,
	take
}                                from 'rxjs/operators';


@Injectable( { providedIn: 'root' } )
export class RouterService {

	private currentActiveRoute: RouteDefinition;

	constructor(
		protected readonly store$: Store,
		private readonly router: Router,
		private readonly logger: LoggerService
	) {
		this.subscribeToStoreEvents();
	}

	get helpers() {
		return {
			getFallbackTab: this.getFallbackTab.bind( this ),
			isValidTab: this.isValidTab.bind( this ),
			validateTabWithFallback: this.validateTabWithFallback.bind( this )
		};
	}

	// HELPERS ######################################################
	private isValidTab(
		params: {
			allowedDetailsTabs: AllowedDetailsTabEnum[],
			tab: AllowedDetailsTabEnum
		}
	): boolean {
		return params.allowedDetailsTabs.includes( params.tab );
	}

	private validateTabWithFallback(
		params: {
			allowedDetailsTabs: AllowedDetailsTabEnum[],
			enforcedFallbackTab?: AllowedDetailsTabEnum,
			redirect?: {
				enforceOnValidTabCheckResult: boolean,
				navigateCallback: ( tab: AllowedDetailsTabEnum ) => void,
			},
			tab: AllowedDetailsTabEnum
		}
	): AllowedDetailsTabEnum {

		// validate tab and sets fallback
		const isValidTab: boolean = this.isValidTab( params );
		const validTab: AllowedDetailsTabEnum =
			isValidTab
			? params.tab
			: this.getFallbackTab( params );

		// redirects optionally on invalid or when enforced
		const redirect: boolean =
			!isValidTab
			|| params.redirect?.enforceOnValidTabCheckResult;

		if ( redirect && params.redirect?.navigateCallback ) {
			params.redirect.navigateCallback( validTab );
		}

		return validTab;
	}


	private getFallbackTab(
		params: {
			allowedDetailsTabs: AllowedDetailsTabEnum[],
			enforcedFallbackTab?: AllowedDetailsTabEnum
		}
	): AllowedDetailsTabEnum {
		return params.enforcedFallbackTab ?? params.allowedDetailsTabs[ 0 ];
	}

	// STORE & NAVIGATION ######################################################
	private subscribeToStoreEvents(): void {
		this.store$.pipe( select( routerStore.selectors.selectActiveRoute ) )
		.subscribe( ( currentActiveRouteDefinition: RouteDefinition ) => {
			this.currentActiveRoute = currentActiveRouteDefinition;
		} );
	}

	private navigate(
		routeDefinition: RouteDefinition,
		caller: string,
		skipLocationChange?: boolean
	): Promise<boolean> {
		this.logger.debug( caller, 'routeDefinition', routeDefinition, 'entitiesForStoreUpdate' );
		const { basePath, path, params, queryParams } = routeDefinition;
		let routePath: string = basePath;

		if ( path ) {
			routePath += `/${ path }`;
			if ( params ) {
				Object.keys( params ).map( routeMapKey => {
					routePath = routePath.replace( ':' + routeMapKey, params[ routeMapKey ] );
				} );
			}
		}

		this.logger.debug( `navigating to route: ${ routePath } | queryParams:`, queryParams );

		return queryParams
					 ? this.router.navigate( [ routePath ], { ...{ queryParams, skipLocationChange } } )
					 : this.router.navigateByUrl( routePath, { skipLocationChange } );
	}

	private navigationSucceedHandler( previousRoute: RouteDefinition, caller: string ): void {
		this.store$.dispatch( routerStore.actions.removeOne( { entityId: this.currentActiveRoute?.id, caller: '' } ) );

		previousRoute = {
			...previousRoute,
			isActiveRoute: true
		};

		this.store$.dispatch( routerStore.actions.upsertOne( { entity: previousRoute, caller: caller + '' } ) );
	}

	private generateFallbackPreviousRouteFromCurrentRouteState(): RouteDefinition {
		const routeSnapshot = this.router.routerState.snapshot;
		const urlSegments = routeSnapshot?.url?.split( '/' );

		/*
		 * If the `urlSegments` array is empty, then it doesn't matter which index
		 * is assigned, because any access to the array will yield `undefined`
		 */
		const urlSegmentStartIndex = urlSegments.length > 0 &&
																 urlSegments[ 0 ].length > 0
																 ? 0
																 : 1;

		const basePath = urlSegments[ urlSegmentStartIndex ] as RouteBasePath ?? null;

		return {
			id: '0',
			isActiveRoute: false,
			basePath,
			path: '' as ApplicationRoutes
		};
	}

	public clearRoutes( caller: string ): void {
		this.store$.dispatch(
			routerStore.actions.removeAll( {
				caller: caller + ' | RouterService:clearRoutes'
			} )
		);
	}

	public getFallbackRoute( basePath: RouteBasePath, path: ApplicationRoutes, params?: IRouteParams ): RouteDefinition {
		const fallbackRoute = new RouteDefinition();
		return {
			...fallbackRoute,
			basePath,
			path,
			params
		};
	}

	public navigateBack( fallbackRoute: RouteDefinition, caller: string ): void {
		this.logger.debug( caller );

		this.store$.pipe( select( routerStore.selectors.selectPreviousRoute ) )
		.pipe( take( 1 ) )
		.subscribe( ( previousRoute: RouteDefinition ) => {

			if ( !previousRoute && !fallbackRoute ) {
				previousRoute = this.generateFallbackPreviousRouteFromCurrentRouteState();
			}

			this.navigate(
				previousRoute ?? fallbackRoute,
				caller + ' | RouterService:navigateBack'
			)
			.then( () => {
				this.navigationSucceedHandler( previousRoute, caller );
			} )
			.catch( ( error ) => {
				this.logger.warn( 'routing failed', error, previousRoute );
				this.logger.info( 'routing trying fallback route', fallbackRoute );
				this.navigate(
					fallbackRoute,
					caller + ' | RouterService:navigateBack fallback'
				)
				.then( () => {
					this.navigationSucceedHandler( previousRoute, caller );
				} )
				.catch( ( fallBackError ) => {
					this.logger.warn( 'fallback routing failed', fallBackError, previousRoute );
				} );
			} );
		} );
	}

	public navigateToNextRoute(
		routeDefinition: IRouteDefinition,
		caller: string,
		skipStoreUpdate: boolean = false,
		skipLocationChange: boolean = false,
		skipRoutesComparisonCheck: boolean = false,
		skipStorePreviousRoute: boolean = false
	): void {
		this.logger.debug( caller, 'routeDefinition', routeDefinition );

		const entitiesForStoreUpdate: RouteDefinition[] = [];

		let currentActiveToPreviousRoute = new RouteDefinition();

		if ( this.currentActiveRoute ) {
			currentActiveToPreviousRoute = this.currentActiveRoute;
		} else {
			currentActiveToPreviousRoute = this.generateFallbackPreviousRouteFromCurrentRouteState();
		}

		this.store$.pipe( select( routerStore.selectors.adapterSelector.total ) )
		.pipe( take( 1 ) )
		.subscribe( ( total: number ) => {

			let basePathTmp = currentActiveToPreviousRoute.basePath;
			if ( !basePathTmp ) {
				if ( routeDefinition?.basePath ) {
					basePathTmp = routeDefinition?.basePath?.split( '/' )[ 0 ] as RouteBasePath;
				} else {
					this.logger.warn( 'Empty basePath => routeDefinition:  ', routeDefinition );
				}
			}

			currentActiveToPreviousRoute = {
				...currentActiveToPreviousRoute,
				id: currentActiveToPreviousRoute.id ?? `${ total }`,
				isActiveRoute: false,
				path: (
					currentActiveToPreviousRoute.path ?? '' as ApplicationRoutes
				),
				basePath: basePathTmp
			};

			if ( !skipStorePreviousRoute ) {
				entitiesForStoreUpdate.push( currentActiveToPreviousRoute );
			}


			const nextRouteDefinition: RouteDefinition = {
				...routeDefinition,
				isActiveRoute: true,
				id: `${ !this.currentActiveRoute ? 1 : Number.parseInt( currentActiveToPreviousRoute?.id, 10 ) + 1 }`
			};

			// stop navigation if same route
			const currentActiveToPreviousRouteString =
				[
					currentActiveToPreviousRoute?.basePath,
					currentActiveToPreviousRoute?.path,
					JSON.stringify( currentActiveToPreviousRoute?.params )
				].join( '' );

			const nextRouteString =
				[
					nextRouteDefinition?.basePath,
					nextRouteDefinition?.path,
					JSON.stringify( nextRouteDefinition?.params )
				].join( '' );

			if (
				currentActiveToPreviousRoute?.path?.length && // route children are compared only
				currentActiveToPreviousRouteString === nextRouteString &&
				!skipRoutesComparisonCheck
			) {
				this.logger.warn(
					'Navigation aborted by RoutesComparisonCheck',
					'currentActiveToPreviousRouteString',
					currentActiveToPreviousRouteString,
					'nextRouteString',
					nextRouteString
				);
				return;
			}


			entitiesForStoreUpdate.push( nextRouteDefinition );

			this.navigate( nextRouteDefinition, caller + ' | RouterService:navigateToNextRoute', skipLocationChange )
			.then( () => {
				if ( !skipStoreUpdate ) {

					if ( nextRouteDefinition.replaceLastStoredRouteWithNew ) {
						const prevRouteId = entitiesForStoreUpdate.shift().id;
						entitiesForStoreUpdate[ 0 ] = { ...nextRouteDefinition, id: prevRouteId };
					}

					this.store$.dispatch(
						routerStore.actions.upsertMany( {
							entities: entitiesForStoreUpdate,
							caller: caller + '->navigate'
						} )
					);
				}
			} )
			.catch( ( error ) => {
				this.logger.error( error );
				this.logger.warn( `routing to nextRouteDefinition failed`, nextRouteDefinition );
			} );
		} );
	}

	public hasRouteByIdTheSameBasePath( routeId: string, basePath: RouteBasePath ): Observable<boolean> {
		return this.store$
		.pipe(
			select( routerStore.selectors.selectRouteById( routeId ) ),
			map( ( routeDefinition: RouteDefinition ) => routeDefinition?.basePath === basePath )
		);
	}

	public getCurrentActiveRoute(): RouteDefinition {
		return this.currentActiveRoute;
	}

	public getPreviousRoute(): RouteDefinition {
		let previousRoute: RouteDefinition;

		this.store$.pipe( select( routerStore.selectors.selectPreviousRoute ) )
		.pipe( take( 1 ) )
		.subscribe( ( storePreviousRoute: RouteDefinition ) => {
			previousRoute = storePreviousRoute;
		} );

		return previousRoute;
	}

	/**
	 * checks for existing firstChild and returns if exists firstChilds snapshot (case on nested routes)
	 * if no firstChild exists snapshot of activatedRoute param is returned (case parent routes)
	 */
	public getFirstChildActivatedRouteSnapshotIfExist( activatedRoute: ActivatedRoute ): ActivatedRouteSnapshot {
		return activatedRoute.firstChild
					 ? activatedRoute.firstChild.snapshot
					 : activatedRoute.snapshot;
	}
}
